import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { localization } from '@ocean/configs'
import { CommonUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { ApiAction, createApiAction, MiddlewareApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import { RangePeriod } from 'components/common/BigCalendar/types'
import { getVirtualEvent } from 'modules/virtualClassrooms/common/api/managerService'
import { namespace } from 'modules/virtualClassrooms/common/localization'
import { VirtualEventRole } from 'modules/virtualClassrooms/common/types/manager'
import {
  AddVirtualEventWaitListPersonsModel,
  RemoveVirtualEventWaitListPersonsModel,
} from 'modules/virtualClassrooms/common/types/waitList'
import {
  getVirtualEventScheduleForCalendar,
  getListSchedulesForCalendar,
  getListVirtualEventRoomSettings,
  updateVirtualEventSchedule,
  getListVirtualEventScheduleEnrolledPersons,
  addVirtualEventScheduleEnrolledPersons,
  removeVirtualEventScheduleEnrolledPersons,
  getListVirtualEventWaitListPersons,
  addVirtualEventWaitListPersons,
  removeVirtualEventWaitListPersons,
  cancelVirtualEventSchedule,
  publishVirtualEventSchedule,
} from 'modules/virtualClassrooms/manager/Schedules/api/schedulesService'
import { VirtualEventsListSchedule } from 'modules/virtualClassrooms/manager/Schedules/types'
import {
  EnrolledPerson,
  UpdatedSchedule,
  UpdateScheduleModel,
  AddScheduleEnrolledPersonsModel,
  RemoveScheduleEnrolledPersonsModel,
} from 'modules/virtualClassrooms/manager/Schedules/types/editor'
import { PlannerUtils } from 'modules/virtualClassrooms/manager/Schedules/utils'
import {
  EditorState,
  ApiVirtualEventDetailsResponse,
  ApiVirtualEventScheduleDetailsResponse,
  ApiListVirtualEventSchedulesResponse,
  ApiListVirtualEventRoomSettingsResponse,
  ApiListVirtualEventScheduleEnrolledPersonsResponse,
  ReloadListVirtualEventScheduleEnrolledPersonsParams,
  ApiListVirtualEventWaitListPersonsResponse,
  ReloadListVirtualEventWaitListPersonsParams,
  ReloadEventScheduleDetailsParams,
} from './types'

const { i18n } = localization

const I18_PATH = `${namespace}:manager.schedules.editor`

const initialState: EditorState = {
  eventDetailsData: {},
  scheduleDetailsData: {},
  scheduleList: {},
  roomSettings: {},
  roomSettingId: undefined,
  scheduleEditorData: {
    timeZone: undefined,
    range: null,
    updatedSchedule: null,
    removedScheduleId: null,
  },
  updateScheduleRequest: {},
  listVirtualEventScheduleEnrolledPersons: {},
  addVirtualEventScheduleEnrolledPersonsRequest: {},
  removeVirtualEventScheduleEnrolledPersonsRequest: {},
  listVirtualEventWaitListPersons: {},
  addVirtualEventWaitListPersonsRequest: {},
  removeVirtualEventWaitListPersonsRequest: {},
  scheduleCancelRequest: {},
  schedulePublishRequest: {},
}

const schedulesEditorSlice = createSlice({
  name: 'virtualClassroomManagerSchedulesEditor',
  initialState,
  reducers: {
    eventDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiVirtualEventDetailsResponse>>
    ) {
      state.eventDetailsData = action.payload
    },
    scheduleDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiVirtualEventScheduleDetailsResponse>>
    ) {
      state.scheduleDetailsData = action.payload
    },
    listVirtualEventSchedulesLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListVirtualEventSchedulesResponse>>
    ) {
      state.scheduleList = action.payload
    },
    setInitialRoomSettingId(state, action: PayloadAction<string>) {
      state.roomSettingId = action.payload
    },
    virtualEventRoomSettingsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListVirtualEventRoomSettingsResponse>>
    ) {
      state.roomSettings = action.payload
    },
    setTimeZone(state, action: PayloadAction<string | undefined>) {
      state.scheduleEditorData.timeZone = action.payload
    },
    setRange(state, action: PayloadAction<RangePeriod>) {
      state.scheduleEditorData.range = action.payload
    },
    updateEditorSchedule(state, action: PayloadAction<UpdatedSchedule>) {
      state.scheduleEditorData.updatedSchedule = action.payload
      state.scheduleEditorData.removedScheduleId = initialState.scheduleEditorData.removedScheduleId
    },
    removeEditorSchedule(state, action: PayloadAction<string>) {
      state.scheduleEditorData.removedScheduleId = action.payload
      state.scheduleEditorData.updatedSchedule = initialState.scheduleEditorData.updatedSchedule
    },
    resetEditorScheduleChanges(state) {
      state.scheduleEditorData.updatedSchedule = initialState.scheduleEditorData.updatedSchedule
      state.scheduleEditorData.removedScheduleId = initialState.scheduleEditorData.removedScheduleId
    },
    updateScheduleRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.updateScheduleRequest = action.payload
    },
    clearEditorData(state) {
      state.eventDetailsData = initialState.eventDetailsData
      state.scheduleDetailsData = initialState.scheduleDetailsData
      state.scheduleEditorData = initialState.scheduleEditorData
      state.roomSettings = initialState.roomSettings
    },
    listVirtualEventScheduleEnrolledPersonsLoaded(
      state,
      action: PayloadAction<
        ApiMiddlewarePayload<ApiListVirtualEventScheduleEnrolledPersonsResponse>
      >
    ) {
      state.listVirtualEventScheduleEnrolledPersons = action.payload
    },
    addVirtualEventScheduleEnrolledPersonsRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload>
    ) {
      state.addVirtualEventScheduleEnrolledPersonsRequest = action.payload
    },
    removeVirtualEventScheduleEnrolledPersonsRequested(
      state,
      action: PayloadAction<ApiMiddlewarePayload>
    ) {
      state.removeVirtualEventScheduleEnrolledPersonsRequest = action.payload
    },
    listVirtualEventWaitListPersonsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiListVirtualEventWaitListPersonsResponse>>
    ) {
      state.listVirtualEventWaitListPersons = action.payload
    },
    addVirtualEventWaitListPersonsRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.addVirtualEventWaitListPersonsRequest = action.payload
    },
    removeVirtualEventWaitListPersonsRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.removeVirtualEventWaitListPersonsRequest = action.payload
    },
    scheduleCancelRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.scheduleCancelRequest = action.payload
    },
    schedulePublishRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.schedulePublishRequest = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  eventDetailsLoaded,
  scheduleDetailsLoaded,
  listVirtualEventSchedulesLoaded,
  setInitialRoomSettingId,
  virtualEventRoomSettingsLoaded,
  updateScheduleRequested,
  resetEditorScheduleChanges,
  listVirtualEventScheduleEnrolledPersonsLoaded,
  addVirtualEventScheduleEnrolledPersonsRequested,
  removeVirtualEventScheduleEnrolledPersonsRequested,
  listVirtualEventWaitListPersonsLoaded,
  addVirtualEventWaitListPersonsRequested,
  removeVirtualEventWaitListPersonsRequested,
  schedulePublishRequested,
  scheduleCancelRequested,
} = schedulesEditorSlice.actions
export const {
  clearEditorData,
  setTimeZone,
  setRange,
  updateEditorSchedule,
  removeEditorSchedule,
} = schedulesEditorSlice.actions
export default schedulesEditorSlice.reducer

export const loadEventDetailsData = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getVirtualEvent(eventId),
    },
    payloadAction: eventDetailsLoaded,
  })
}

export const handleLoadExerciseDetailsData =
  (response: ApiMiddlewarePayload<ApiVirtualEventScheduleDetailsResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result) {
      const { eventId } = result.details
      dispatch(loadEventDetailsData(eventId) as MiddlewareApiAction)
    }
    dispatch(scheduleDetailsLoaded(response))
  }

export const loadEventScheduleDetailsData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getVirtualEventScheduleForCalendar(scheduleId),
    },
    payloadAction: scheduleDetailsLoaded,
    nextAction: handleLoadExerciseDetailsData,
  })
}

const handleReloadEventScheduleDetails =
  (response: ApiMiddlewarePayload, params: ReloadEventScheduleDetailsParams): AppThunk =>
  async dispatch => {
    const { payloadAction, scheduleId } = params
    const { error } = response
    dispatch(payloadAction(response))
    if (!error) {
      dispatch(reloadEventScheduleDetailsData(scheduleId) as MiddlewareApiAction)
    }
  }

export const reloadEventScheduleDetailsData = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getVirtualEventScheduleForCalendar(scheduleId),
    },
    payloadAction: scheduleDetailsLoaded,
    getResultToKeep: ({ virtualClassroomManagerSchedulesEditor }) =>
      virtualClassroomManagerSchedulesEditor.scheduleDetailsData.result,
    shouldShowGlobalLoading: true,
  })
}

const handleReloadEventScheduleDetailsAndEnrolledPersonsAndWaitList =
  (response: ApiMiddlewarePayload, params: ReloadEventScheduleDetailsParams): AppThunk =>
  async dispatch => {
    const { payloadAction, scheduleId } = params
    const { error } = response
    dispatch(payloadAction(response))
    if (error) return
    dispatch(
      reloadEventScheduleDetailsAndEnrolledPersonsAndWaitListData(scheduleId) as MiddlewareApiAction
    )
  }

export const reloadEventScheduleDetailsAndEnrolledPersonsAndWaitListData = (
  scheduleId: Guid
): ApiAction => {
  return createApiAction({
    apiCalls: {
      details: () => getVirtualEventScheduleForCalendar(scheduleId),
    },
    payloadAction: scheduleDetailsLoaded,
    getResultToKeep: ({ virtualClassroomManagerSchedulesEditor }) =>
      virtualClassroomManagerSchedulesEditor.scheduleDetailsData.result,
    nextAction: {
      action: handleReloadVirtualEventScheduleEnrolledPersonsAndWaitlist,
      params: { scheduleId, payloadAction: scheduleDetailsLoaded },
    },
    shouldShowGlobalLoading: true,
  })
}

export const handleLoadListVirtualEventRoomSettings =
  (response: ApiMiddlewarePayload<ApiListVirtualEventRoomSettingsResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (!error && result) {
      PlannerUtils.setResources(result.settings.roomSettings)
    }
    dispatch(virtualEventRoomSettingsLoaded(response))
  }

export const loadListVirtualEventRoomSettings = (): ApiAction => {
  return createApiAction({
    apiCalls: {
      settings: () => getListVirtualEventRoomSettings(),
    },
    payloadAction: virtualEventRoomSettingsLoaded,
    nextAction: handleLoadListVirtualEventRoomSettings,
    shouldCallApi: () => !PlannerUtils.resources.length,
  })
}

export const loadListVirtualEventSchedules = (
  range: RangePeriod,
  scheduleId: Guid,
  timeZone: String
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListSchedulesForCalendar(range, timeZone),
    },
    payloadAction: listVirtualEventSchedulesLoaded,
    nextAction: {
      action: handleSetInitialRoomSettingId,
      params: scheduleId,
    },
  })
}

export const handleSetInitialRoomSettingId =
  (
    response: ApiMiddlewarePayload<ApiListVirtualEventSchedulesResponse>,
    scheduleId: Guid
  ): AppThunk =>
  async dispatch => {
    const { error, result } = response
    const { schedules } = result?.list || {}
    dispatch(listVirtualEventSchedulesLoaded(response))
    const { roomSettingId } = schedules?.find(schedule => schedule.id === scheduleId) || {}
    if (error || !roomSettingId) return
    dispatch(setInitialRoomSettingId(roomSettingId))
  }

export const reloadListVirtualEventSchedules = (
  range: RangePeriod,
  timeZone: String
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListSchedulesForCalendar(range, timeZone),
    },
    payloadAction: listVirtualEventSchedulesLoaded,
    shouldShowGlobalLoading: false,
    getResultToKeep: ({ virtualClassroomManagerSchedulesEditor }) =>
      virtualClassroomManagerSchedulesEditor.scheduleList.result,
  })
}

export const handleUpdateEventScheduleRequest =
  (response: ApiMiddlewarePayload): AppThunk =>
  async (dispatch, getState) => {
    dispatch(updateScheduleRequested(response))
    if (!response.error) {
      const { virtualClassroomManagerSchedulesEditor } = getState()
      const { scheduleEditorData } = virtualClassroomManagerSchedulesEditor
      const { startDate, endDate } = scheduleEditorData.range as RangePeriod
      const timeZoneId = scheduleEditorData.timeZone as String
      dispatch(
        reloadListVirtualEventSchedules({ startDate, endDate }, timeZoneId) as MiddlewareApiAction
      )
      dispatch(resetEditorScheduleChanges())
    }
  }

export const updateEventSchedule = (params: UpdateScheduleModel): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => updateVirtualEventSchedule(params),
    },
    payloadAction: updateScheduleRequested,
    nextAction: handleUpdateEventScheduleRequest,
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const getEditorMergedScheduleUpdates = createSelector(
  (
    scheduleList: VirtualEventsListSchedule[] = [],
    updatedSchedule: Nullable<UpdatedSchedule>,
    removedScheduleId: Nullable<Guid>
  ) => ({ scheduleList, updatedSchedule, removedScheduleId }),
  ({ scheduleList, updatedSchedule, removedScheduleId }) => {
    let changedSchedule: VirtualEventsListSchedule | undefined

    if (updatedSchedule) {
      const { startDate, endDate, roomSettingId, scheduleId, title } = updatedSchedule
      const { event, ...restDefaultScheduleParams } =
        PlannerUtils.DEFAULT_VIRTUAL_EVENTS_LIST_SCHEDULE
      changedSchedule = {
        ...restDefaultScheduleParams,
        event: {
          ...event,
          title,
        },
        id: scheduleId,
        startDate,
        endDate,
        roomSettingId,
      } as VirtualEventsListSchedule
    }

    if (changedSchedule && scheduleList.length === 0) {
      return [changedSchedule]
    }

    return scheduleList.reduce((result, schedule) => {
      if (removedScheduleId === schedule.id) return result
      if (changedSchedule && schedule.id === changedSchedule.id) {
        result.push(changedSchedule)
        return result
      }
      result.push(schedule)
      return result
    }, [] as VirtualEventsListSchedule[])
  }
)

export const getScheduleMappedToPlanner = createSelector(
  (scheduleList: VirtualEventsListSchedule[] = [], scheduleId: Guid | undefined) => ({
    scheduleList,
    scheduleId,
  }),
  ({ scheduleList, scheduleId }) => PlannerUtils.mapScheduleToPlanner(scheduleList, scheduleId)
)

export const getEnrolledPersonsPartition = createSelector(
  (enrolledPersons: EnrolledPerson[] = []) => enrolledPersons,
  enrolledPersons =>
    CommonUtils.partition<EnrolledPerson>(
      enrolledPersons,
      (person: EnrolledPerson) => person.eventRole === VirtualEventRole.Presenter
    )
)

export const getEnrolledPersonsIds = createSelector(
  (enrolledPersons: EnrolledPerson[] = []) => enrolledPersons,
  enrolledPersons => enrolledPersons.map(({ userId }) => userId)
)

export const loadListVirtualEventScheduleEnrolledPersons = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListVirtualEventScheduleEnrolledPersons(eventId),
    },
    payloadAction: listVirtualEventScheduleEnrolledPersonsLoaded,
  })
}

const handleReloadListVirtualEventScheduleEnrolledPersons =
  (
    response: ApiMiddlewarePayload,
    params: ReloadListVirtualEventScheduleEnrolledPersonsParams
  ): AppThunk =>
  async dispatch => {
    const { scheduleId, payloadAction } = params
    const { error } = response
    dispatch(payloadAction(response))
    if (error) return
    dispatch(reloadListVirtualEventScheduleEnrolledPersons(scheduleId) as MiddlewareApiAction)
  }

export const reloadListVirtualEventScheduleEnrolledPersons = (scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListVirtualEventScheduleEnrolledPersons(scheduleId),
    },
    payloadAction: listVirtualEventScheduleEnrolledPersonsLoaded,
    getResultToKeep: ({ virtualClassroomManagerSchedulesEditor }) =>
      virtualClassroomManagerSchedulesEditor.listVirtualEventScheduleEnrolledPersons.result,
    shouldShowGlobalLoading: true,
  })
}

export const addScheduleEnrolledPersons = (params: AddScheduleEnrolledPersonsModel): ApiAction => {
  const { scheduleId } = params
  return createApiAction({
    apiCalls: {
      request: () => addVirtualEventScheduleEnrolledPersons(params),
    },
    payloadAction: addVirtualEventScheduleEnrolledPersonsRequested,
    nextAction: {
      action: handleReloadVirtualEventScheduleEnrolledPersonsAndWaitlist,
      params: { scheduleId, payloadAction: addVirtualEventScheduleEnrolledPersonsRequested },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

const handleReloadVirtualEventScheduleEnrolledPersonsAndWaitlist =
  (
    response: ApiMiddlewarePayload,
    params: ReloadListVirtualEventScheduleEnrolledPersonsParams
  ): AppThunk =>
  async (dispatch, getState) => {
    const { scheduleId, payloadAction } = params
    const { error } = response
    // Need "await" to set isLoading=false before isGlobalLoading is set to false
    dispatch(payloadAction(response))

    if (error) return
    const { eventId } =
      getState().virtualClassroomManagerSchedulesEditor.scheduleDetailsData.result?.details || {}
    eventId &&
      dispatch(
        reloadListVirtualEventScheduleEnrolledPersonsAndWaitlist(
          scheduleId,
          eventId
        ) as MiddlewareApiAction
      )
  }

export const reloadListVirtualEventScheduleEnrolledPersonsAndWaitlist = (
  scheduleId: Guid,
  eventId: Guid
): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListVirtualEventScheduleEnrolledPersons(scheduleId),
    },
    payloadAction: listVirtualEventScheduleEnrolledPersonsLoaded,
    getResultToKeep: ({ virtualClassroomManagerSchedulesEditor }) =>
      virtualClassroomManagerSchedulesEditor.listVirtualEventScheduleEnrolledPersons.result,
    nextAction: {
      action: handleReloadListVirtualEventWaitListPersons,
      params: { eventId, payloadAction: listVirtualEventScheduleEnrolledPersonsLoaded },
    },
    shouldShowGlobalLoading: true,
  })
}

export const removeScheduleEnrolledPersons = (
  params: RemoveScheduleEnrolledPersonsModel
): ApiAction => {
  const { scheduleId } = params
  return createApiAction({
    apiCalls: {
      request: () => removeVirtualEventScheduleEnrolledPersons(params),
    },
    payloadAction: removeVirtualEventScheduleEnrolledPersonsRequested,
    nextAction: {
      action: handleReloadListVirtualEventScheduleEnrolledPersons,
      params: { scheduleId, payloadAction: removeVirtualEventScheduleEnrolledPersonsRequested },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const loadListVirtualEventWaitListPersons = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListVirtualEventWaitListPersons(eventId),
    },
    payloadAction: listVirtualEventWaitListPersonsLoaded,
  })
}

const handleReloadListVirtualEventWaitListPersons =
  (response: ApiMiddlewarePayload, params: ReloadListVirtualEventWaitListPersonsParams): AppThunk =>
  async dispatch => {
    const { eventId, payloadAction } = params
    const { error } = response
    dispatch(payloadAction(response))
    if (!error) {
      dispatch(reloadListVirtualEventWaitListPersons(eventId) as MiddlewareApiAction)
    }
  }

export const reloadListVirtualEventWaitListPersons = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      list: () => getListVirtualEventWaitListPersons(eventId),
    },
    payloadAction: listVirtualEventWaitListPersonsLoaded,
    getResultToKeep: ({ virtualClassroomManagerSchedulesEditor }) =>
      virtualClassroomManagerSchedulesEditor.listVirtualEventWaitListPersons.result,
    shouldShowGlobalLoading: true,
  })
}

export const addWaitListPersons = (params: AddVirtualEventWaitListPersonsModel): ApiAction => {
  const { eventId } = params
  return createApiAction({
    apiCalls: {
      request: () => addVirtualEventWaitListPersons(params),
    },
    payloadAction: addVirtualEventWaitListPersonsRequested,
    nextAction: {
      action: handleReloadListVirtualEventWaitListPersons,
      params: { eventId, payloadAction: addVirtualEventWaitListPersonsRequested },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const removeWaitListPersons = (
  params: RemoveVirtualEventWaitListPersonsModel
): ApiAction => {
  const { eventId } = params
  return createApiAction({
    apiCalls: {
      request: () => removeVirtualEventWaitListPersons(params),
    },
    payloadAction: removeVirtualEventWaitListPersonsRequested,
    nextAction: {
      action: handleReloadListVirtualEventWaitListPersons,
      params: { eventId, payloadAction: removeVirtualEventWaitListPersonsRequested },
    },
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const publishSchedule = (scheduleId: Guid) => {
  return createApiAction({
    apiCalls: {
      request: () => publishVirtualEventSchedule(scheduleId),
    },
    payloadAction: schedulePublishRequested,
    nextAction: {
      action: handleReloadEventScheduleDetails,
      params: { scheduleId, payloadAction: schedulePublishRequested },
    },
    enableErrorNotification: true,
    successMessage: i18n.t(`${I18_PATH}.successPublish`),
    shouldShowGlobalLoading: true,
  })
}

export const cancelSchedule = (scheduleId: Guid) => {
  return createApiAction({
    apiCalls: {
      request: () => cancelVirtualEventSchedule(scheduleId),
    },
    payloadAction: scheduleCancelRequested,
    nextAction: {
      action: handleReloadEventScheduleDetailsAndEnrolledPersonsAndWaitList,
      params: { scheduleId, payloadAction: scheduleCancelRequested },
    },
    enableErrorNotification: true,
    successMessage: i18n.t(`${I18_PATH}.successCancel`),
    shouldShowGlobalLoading: true,
  })
}
